import { AccessPermission } from '@/api';
import type { SelectOption } from 'naive-ui';

/** Single-bit permission flags (bitwise). NONE=0, READ=1, WRITE=2, ADMIN=4. Combined e.g. READ|WRITE=3. */
export const ACCESS_PERMISSION_READ = AccessPermission._1;
export const ACCESS_PERMISSION_WRITE = AccessPermission._2;
export const ACCESS_PERMISSION_ADMIN = AccessPermission._4;

/**
 * The API types `permissions` as `AccessPermission`, but the backend's IntFlag
 * is generated as a closed enum of single bits, so a combined mask (READ|WRITE
 * = 3) has no member. Send masks through here, at the API boundary only.
 */
export function toAccessPermission(mask: number): AccessPermission {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment -- combined bits are valid on the wire; the generated enum can't list them
  return mask;
}

/** Human-readable labels for each permission bit (contract/user/role shares, etc.) */
export const ACCESS_PERMISSION_LABELS: Record<AccessPermission, string> = {
  [AccessPermission._0]: 'None',
  [AccessPermission._1]: 'Read',
  [AccessPermission._2]: 'Write',
  [AccessPermission._4]: 'Admin',
};

/** Flags available for multi-select (excludes None). Use these to build/parse bitmask. */
export const ACCESS_PERMISSION_FLAGS = [
  { value: AccessPermission._1, label: 'Read' },
  { value: AccessPermission._2, label: 'Write' },
  { value: AccessPermission._4, label: 'Admin' },
] as const;

/** Return display label for a single permission value */
export function getAccessPermissionLabel(perm: AccessPermission): string {
  return ACCESS_PERMISSION_LABELS[perm];
}

/**
 * Given a bitmask (e.g. 3 = Read|Write), return array of human-readable labels.
 * Use for displaying combined permissions on cards.
 */
export function getAccessPermissionLabels(permissions: number): string[] {
  if (permissions === 0) return ['None'];
  return ACCESS_PERMISSION_FLAGS.filter(f => (permissions & f.value) !== 0).map(f => f.label);
}

/**
 * Access as people think of it — a level, not three bits to tick. Each level
 * includes the ones below it. The backend checks bits independently (Admin
 * alone does not grant Read), so these three are the masks that behave as
 * their names say.
 */
export const ACCESS_LEVELS = [
  { value: AccessPermission._1, label: 'Can view' },
  { value: AccessPermission._1 | AccessPermission._2, label: 'Can edit' },
  {
    value: AccessPermission._1 | AccessPermission._2 | AccessPermission._4,
    label: 'Full access',
  },
] as const;

/** The levels as select options. */
export const ACCESS_LEVEL_OPTIONS: SelectOption[] = ACCESS_LEVELS.map(level => ({
  label: level.label,
  value: level.value,
}));

/**
 * A role's organisation-wide access (every contract / every contractual
 * party): the share levels plus "No access", which there means "only what is
 * shared individually".
 */
export const ORG_WIDE_ACCESS_OPTIONS: SelectOption[] = [
  { label: 'No access', value: AccessPermission._0 },
  ...ACCESS_LEVEL_OPTIONS,
];

/**
 * Display a share's access: its level name ("Can edit"), or — for a mask the
 * old per-bit checkboxes could save that is no level ("Read, Admin") — its
 * bits, so nothing is shown as more or less than it grants.
 */
export function formatAccessPermissions(permissions: number): string {
  if (permissions === 0) return 'No access';
  const level = ACCESS_LEVELS.find(candidate => candidate.value === permissions);
  return level ? level.label : getAccessPermissionLabels(permissions).join(', ');
}

/** Options for single permission select (legacy); excludes None */
export const ACCESS_PERMISSION_SELECT_OPTIONS: SelectOption[] = [
  { label: 'Read', value: AccessPermission._1 },
  { label: 'Write', value: AccessPermission._2 },
  { label: 'Admin', value: AccessPermission._4 },
];

/** Options for multi-select (checkboxes); Read, Write, Admin */
export const ACCESS_PERMISSION_MULTI_OPTIONS = ACCESS_PERMISSION_FLAGS.map(f => ({
  label: f.label,
  value: f.value,
}));

/**
 * Whether the caller's effective bits on an item (`permissions` on vault
 * folders, files and contracts) include `flag`. The bits are checked one by
 * one, as the backend does. `null`/`undefined` means the response did not
 * compute them: stay optimistic and allow, the backend still refuses.
 */
export function allows(bits: number | null | undefined, flag: number): boolean {
  return bits == null || (bits & flag) !== 0;
}

const DENIED_REASON: Record<number, string> = {
  [AccessPermission._1]: 'Needs view access',
  [AccessPermission._2]: 'Needs edit access',
  [AccessPermission._4]: 'Needs full access',
};

/** Why an action is disabled, when the bits lack `flag`; null when allowed. */
export function deniedReason(bits: number | null | undefined, flag: number): string | null {
  return allows(bits, flag) ? null : (DENIED_REASON[flag] ?? 'Not allowed');
}

/** Disables a menu entry the bits rule out, with the reason as its title. */
export function gateOption<T extends object>(
  option: T,
  bits: number | null | undefined,
  flag: number
): T {
  const reason = deniedReason(bits, flag);
  if (!reason) return option;
  const props = (option as { props?: Record<string, unknown> }).props ?? {};
  return { ...option, disabled: true, props: { ...props, title: reason } };
}
